export interface EstateTask {
  taskId: string;
  title: string;
}

export interface EstateModule {
  id: string;
  title: string;
  description: string;
  tasks: readonly EstateTask[];
}

export const ESTATE_MODULES: readonly EstateModule[] = [
  {
    id: "estate-setup",
    title: "Estate Setup",
    description: "Establish legal authority and get the estate basics in order.",
    tasks: [
      { taskId: "will", title: "Will" },
      { taskId: "trust", title: "Trust" },
      { taskId: "lot", title: "Letters of Testamentary" },
    ],
  },
  {
    id: "real-estate",
    title: "Real Estate",
    description: "Homes, land, and property to transfer or sell.",
    tasks: [
      { taskId: "properties", title: "Properties" },
      { taskId: "property_loans", title: "Property Loans" },
      { taskId: "property_insurance", title: "Property Insurance" },
    ],
  },
  {
    id: "financial-accounts",
    title: "Financial Accounts",
    description: "Bank, credit card, investment, and retirement accounts to transfer or close.",
    tasks: [
      { taskId: "account_types", title: "Account Types" },
      { taskId: "account_balances", title: "Account Balances" },
    ],
  },
  {
    id: "benefits-eligibility",
    title: "Insurance and Benefits",
    description: "Life insurance, pensions, and benefits your family may be entitled to claim.",
    tasks: [
      { taskId: "life_insurance", title: "Life Insurance" },
      { taskId: "ltc_insurance", title: "LTC Insurance" },
      { taskId: "military", title: "Military" },
      { taskId: "employment", title: "Employment" },
    ],
  },
  {
    id: "personal-property",
    title: "Tangible Personal Property",
    description: "Vehicles, jewelry, art, and other belongings to transfer or distribute.",
    tasks: [
      { taskId: "vehicles", title: "Vehicles" },
      { taskId: "valuables", title: "Valuables" },
      { taskId: "deposit_boxes", title: "Deposit Boxes" },
    ],
  },
  {
    id: "other-assets",
    title: "Other Assets",
    description: "Receivables, stock options, royalties, and prepaid services.",
    tasks: [{ taskId: "other_assets", title: "Other Assets" }],
  },
  {
    id: "debts-loans",
    title: "Debts and Loans",
    description: "What's owed by the estate, paid before anything is distributed.",
    tasks: [
      { taskId: "credit_cards", title: "Credit Cards" },
      { taskId: "mortgage", title: "Mortgages" },
      { taskId: "other_loans", title: "Other Loans" },
      { taskId: "other_debts", title: "Other Debts" },
    ],
  },
  {
    id: "expenses",
    title: "Estate Expenses",
    description: "Out-of-pocket costs that may be reimbursable to the estate.",
    tasks: [
      { taskId: "funeral_costs", title: "Funeral Costs" },
      { taskId: "ongoing_costs", title: "Ongoing Costs" },
    ],
  },
] as const;

export const ESTATE_TASK_IDS: readonly string[] = ESTATE_MODULES.flatMap((module) =>
  module.tasks.map((task) => task.taskId),
);

export function getTaskById(
  taskId: string,
): { task: EstateTask; module: EstateModule } | undefined {
  for (const module of ESTATE_MODULES) {
    const task = module.tasks.find((t) => t.taskId === taskId);
    if (task) return { task, module };
  }
  return undefined;
}

export function getModuleById(moduleId: string): EstateModule | undefined {
  return ESTATE_MODULES.find((m) => m.id === moduleId);
}
